import { useField } from 'formik';
import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { DeleteIcon, ViewIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { EvaluationSubmissionGoal } from '@treams/treams-network';
import { Button, FlexGap, Paragraph, Popover, Subheading, tokens } from '@treams/treams-ui';

import { useModalLink } from '../../../navigation/ModalNavigation';

type Props = {
	goal: EvaluationSubmissionGoal;
	name: string;
	setSelectedGoals: (goals: EvaluationSubmissionGoal[]) => void;
	selectedGoals: EvaluationSubmissionGoal[];
	title?: string;
	description?: string | ReactNode;
};

const EvaluationSubmissionReviewGoalsAccordionFooter: FC<Props> = ({
	goal,
	name,
	setSelectedGoals,
	selectedGoals,
}) => {
	const { t } = useTranslation();
	const [{ value }, , { setValue }] = useField<{
		type: graphql.EvaluationSectionItemType.ReviewGoals;
		reviewGoals?: graphql.EvaluationSubmissionItemReviewGoalInput[];
	}>(name);

	const [showConfirm, setShowConfirm] = useState<boolean>(false);

	const remove = (confirmed: boolean) => {
		if (!confirmed) {
			setShowConfirm(false);
			return;
		}

		setSelectedGoals(selectedGoals.filter((selectedGoal) => selectedGoal.goal.id !== goal.goal.id));
		setValue({
			...value,
			reviewGoals: value.reviewGoals?.filter((reviewGoal) => reviewGoal.goalID !== goal.goal.id),
		});
		setShowConfirm(false);
	};

	const link = useModalLink(`/goals/${goal.goal.id}`, true);

	return (
		<Box p={0}>
			<Flex justifyContent="flex-end" p={2}>
				<Button
					small
					mr={2}
					icon={<ViewIcon />}
					title={t('EvaluationSubmission.reviewGoals.detail')}
					type="secondary"
					link={link}
				/>

				<Popover.Target
					visible={showConfirm}
					horizontalAlign="right"
					contents={
						<Box p={`${tokens.padding.container}px`} style={{ width: 300 }}>
							<Subheading mb={2}>{t('reviewGoal.accordionHeader.deleteTitle')}</Subheading>
							<Paragraph mb={3}>{t('reviewGoal.accordionHeader.deleteDescription')}</Paragraph>
							<FlexGap justifyContent="flex-end" gap={tokens.gap.element}>
								<Button
									type="secondary"
									title={t('common.cancel')}
									onClick={(event) => {
										event.stopPropagation();
										remove(false);
									}}
								/>
								<Button
									title={t('common.confirm')}
									onClick={(event) => {
										event.stopPropagation();
										remove(true);
									}}
								/>
							</FlexGap>
						</Box>
					}
					onDismiss={() => remove(false)}
				>
					<Button
						type="destructive"
						small
						icon={<DeleteIcon />}
						title={t('EvaluationSubmission.reviewGoals.delete')}
						onClick={(e) => {
							e.stopPropagation();
							setShowConfirm(true);
						}}
					/>
				</Popover.Target>
			</Flex>
		</Box>
	);
};
export default EvaluationSubmissionReviewGoalsAccordionFooter;
